// dependencies
import React from "react";
import { createPortal } from "react-dom";
import { Toaster, toast } from "sonner";

import {
	faCircleCheck,
	faCircleExclamation,
	faCircleInfo,
	faTriangleExclamation,
} from "@performyard/fontawesome/pro-regular-svg-icons";

// components
import { Icon } from "../../atoms/Icon";
// base css override for toast
import "./toast.css";

type ToasterProps = React.ComponentProps<typeof Toaster>;

/**
 * Toast component that displays a message to the user.Can be used to display success, error, warning, or info messages.
 * `Toast` component is meant to be imported once around the root of the application. While the `toast` function is used to trigger the toast.
 * Example: toast.success("Success message") will trigger the success message toast.
 */
const Toast = ({ ...props }: ToasterProps) => {
	return createPortal(
		<Toaster
			icons={{
				success: <Icon icon={faCircleCheck} color="green-5" size={24} />,
				error: <Icon icon={faCircleExclamation} color="red-5" size={24} />,
				warning: <Icon icon={faTriangleExclamation} color="yellow-5" size={24} />,
				info: <Icon icon={faCircleInfo} color="primary-5" size={24} />,
			}}
			className="absolute bottom-0 flex justify-center"
			position="bottom-center"
			toastOptions={{
				unstyled: true,
				classNames: {
					// variants
					success: "bg-green-1",
					error: "bg-red-1",
					warning: "bg-yellow-1",
					info: "bg-primary-1",

					// general
					toast: "flex gap-1 p-2 rounded-[4px] items-center w-max whitespace-normal word-break-break-word max-w-80vw",
					content: "flex flex-col items-start",
					title: "text-sub-heading font-semi-bold",
					icon: "flex items-center",
				},
			}}
			{...props}
		/>,
		document.body
	);
};

Toast.displayName = "Toast";

export { Toast, toast };
